import { FileUpload } from "primereact/fileupload";
import type { FileUpload as PrimeFileUpload } from "primereact/fileupload";
import "primeicons/primeicons.css";
import { useEffect, useRef, useState } from "react";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import "../FileUpload/FileUploadComponent.scss";
import { ApiEndpoints, Constants } from "../../constants/appConstants";
import httpService from "../../services/httpService";
import { useAppContext } from "../../Context";
import { AxiosError } from "axios";
import {
  HubConnection,
  HubConnectionBuilder,
  HttpTransportType,
  LogLevel,
} from "@microsoft/signalr";
import { Dropdown } from "primereact/dropdown";
import { useAuthContext } from "../../Context/AuthContext";

interface FileMetaInfo {
  fileName: string;
  documentType: string;
  fileSize: number | null;
  fileExtension: string;
  creationDate: string;
  providerId: string;
  isProviderDataFile: boolean;
  measureNumber: string;
  isProviderAttestation: boolean;
}

interface FileUploInfo {
  fileName: any;
  status: string;
  hasError: boolean | undefined;
  file?: any;
  subFiles: any;
  errorMessage?: string;
  statusCode: number | undefined;
  filesCount: number;
}

interface ProviderType {
  providerType: string;
  providerCode: string;
  providerTypeId: number;
}

interface FileType {
  documentType: string;
  documentTypeId: number;
}

export const MultipartFileUploadComponent = (props: any) => {
  const uploadRef = useRef<PrimeFileUpload>(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [files, setFiles] = useState<FileUploInfo[]>([]);

  const { reviewYear: contextReviewYear } = useAppContext();
  const reviewYear = props.reviewYear ?? contextReviewYear;

  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [signalRConnectionId, setSignalRConnectionId] = useState<string>("");

  const [statusMessages, setStatusMessages] = useState<any[]>([]);
  const [filesInfo, setFilesfInfo] = useState<any[]>([]);

  const [reviewYearState, setReviewYear] = useState(0);
  const [providerType, setproviderType] = useState("");
  const [fileType, setFileType] = useState("");
  const [isUploadDisabled, setIsUploadDisabled] = useState(false);
  const [reviewYearOption, setReviewYearOption] = useState(0);

  const { isAdministrator } = useAuthContext();

  const [providerTypeData, setProviderTypeData] = useState<ProviderType[]>([]);
  const [fileTypeData, setfileTypeData] = useState<FileType[]>([]);
  const [isDucLocked, setIsDucLocked] = useState(true);

  const [scanInfo, setScanInfo] = useState<any[]>([]);
  const [isScanEnabled, setIsScanEnabled] = useState(false);

  const [pendingProcessRequests, setPendingProcessRequests] = useState<
    Map<string, () => Promise<any>>
  >(new Map());

  const getSafeConnectionId = () => {
    if (signalRConnectionId) {
      return signalRConnectionId;
    }

    if (connection?.connectionId) {
      return connection.connectionId;
    }

    return "";
  };

  const resetFileSelection = () => {
    setFiles([]);
    setErrorMessage("");
    setIsUploadDisabled(false);
    uploadRef.current?.clear?.();
  };

  useEffect(() => {
    const hubUrl = ApiEndpoints.fileProcessingHub;

    console.log(
      "[MultipartFileUploadComponent] Initializing SignalR hub:",
      hubUrl
    );

    const newConnection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        transport: HttpTransportType.ServerSentEvents,
      })
      .configureLogging(LogLevel.Information)
      .build();

    newConnection
      .start()
      .then(async () => {
        console.log(
          "[MultipartFileUploadComponent] SignalR connection established. client connectionId:",
          newConnection.connectionId
        );

        try {
          const idFromServer =
            await newConnection.invoke<string>("GetConnectionId");

          console.log(
            "[MultipartFileUploadComponent] GetConnectionId returned:",
            idFromServer
          );

          const effectiveId =
            idFromServer ||
            newConnection.connectionId ||
            "";

          setSignalRConnectionId(effectiveId);
        } catch (err) {
          console.error(
            "[MultipartFileUploadComponent] GetConnectionId failed, falling back to client connectionId",
            err
          );

          setSignalRConnectionId(
            newConnection.connectionId || ""
          );
        }

        setConnection(newConnection);
      })
      .catch((err) =>
        console.error(
          "[MultipartFileUploadComponent] Error establishing SignalR:",
          err
        )
      );

    return () => {
      try {
        void newConnection.stop();
      } catch (error) {
        console.error(
          "[MultipartFileUploadComponent] Error stopping SignalR connection:",
          error
        );
      }
    };
  }, []);

  useEffect(() => {
    if (!connection) {
      return;
    }

    const onSendStatus = (message: any) => {
      console.log(
        "[MultipartFileUploadComponent] SendStatus:",
        message
      );

      setStatusMessages((prev) => [
        ...prev,
        message,
      ]);
    };

    const onSendFileInfo = (fileInfo: any) => {
      console.log(
        "[MultipartFileUploadComponent] SendFileInfo:",
        fileInfo
      );

      setFilesfInfo((prev) => [
        ...prev,
        fileInfo,
      ]);
    };

    const onScanInfo = (message: any) => {
      console.log(
        "[MultipartFileUploadComponent] ScanInfo:",
        message
      );

      setScanInfo((prev) => [
        ...prev,
        message,
      ]);
    };

    connection.on(
      "SendStatus",
      onSendStatus
    );

    connection.on(
      "SendFileInfo",
      onSendFileInfo
    );

    connection.on(
      "ScanInfo",
      onScanInfo
    );

    return () => {
      connection.off(
        "SendStatus",
        onSendStatus
      );

      connection.off(
        "SendFileInfo",
        onSendFileInfo
      );

      connection.off(
        "ScanInfo",
        onScanInfo
      );
    };
  }, [connection]);

  useEffect(() => {
    if (
      !statusMessages ||
      statusMessages.length === 0
    ) {
      return;
    }

    const groupedMessages =
      getLatestMessage(statusMessages);

    for (const fileObj of files) {
      const isZipFile =
        fileObj?.fileName
          ?.toLowerCase()
          .endsWith(".zip");

      if (isZipFile) {
        fileObj.subFiles =
          groupedMessages[fileObj.fileName];

        fileObj.hasError =
          fileObj.subFiles?.some(
            (x: any) =>
              x.statusCode === 400
          );

        let status = "Success";
        let statusCode = 0;

        if (
          fileObj.subFiles?.some(
            (x: any) =>
              x.statusCode === 302
          )
        ) {
          status = "Processing...";
          statusCode = 302;
        } else if (
          fileObj.subFiles?.some(
            (x: any) =>
              x.statusCode === 200
          ) &&
          fileObj.hasError
        ) {
          status =
            "Partially Uploaded";
          statusCode = 200;
        } else if (
          fileObj.subFiles?.every(
            (x: any) =>
              x.statusCode === 400
          )
        ) {
          status = "Failed";
          statusCode = 400;
        } else if (
          fileObj.subFiles?.every(
            (x: any) =>
              x.statusCode === 200
          )
        ) {
          status = "Success";
          statusCode = 200;
        }

        fileObj.status = status;
        fileObj.statusCode =
          statusCode;

        setFiles([...files]);
      } else {
        const fileInfo = files.find(
          (f) =>
            f.fileName ===
            fileObj.fileName
        );

        if (fileInfo) {
          const latestMessages =
            groupedMessages[
              fileObj.fileName
            ];

          if (
            latestMessages &&
            latestMessages.length > 0
          ) {
            const latestMessage =
              latestMessages[0];

            fileInfo.status =
              getStatusByStatusCode(
                latestMessage?.statusCode
              );

            fileInfo.hasError =
              latestMessage?.statusCode ===
              400;

            fileInfo.statusCode =
              latestMessage?.statusCode;

            fileInfo.errorMessage =
              latestMessage?.message;
          }

          setFiles([...files]);
        }
      }
    }
  }, [statusMessages]);

  useEffect(() => {
    if (
      !isScanEnabled ||
      pendingProcessRequests.size <= 0 ||
      !scanInfo ||
      scanInfo.length === 0
    ) {
      return;
    }

    console.log(
      "[MultipartFileUploadComponent] Scan effect triggered",
      "pendingProcessRequests:",
      pendingProcessRequests.size,
      "scanInfo:",
      scanInfo.length
    );

    const normalizedScanInfo =
      scanInfo.map((x: any) => ({
        fileName:
          x.fileName || x.FileName,
        isFine:
          typeof x.isFine !==
          "undefined"
            ? x.isFine
            : x.IsFine ?? null,
      }));

    const scannedFiles =
      normalizedScanInfo
        .filter(
          (x: any) =>
            x.fileName &&
            x.isFine === true
        )
        .map(
          (x: any) =>
            x.fileName as string
        );

    const scanFailedFiles =
      normalizedScanInfo
        .filter(
          (x: any) =>
            x.fileName &&
            x.isFine === false
        )
        .map(
          (x: any) =>
            x.fileName as string
        );

    scanFailedFiles.forEach(
      (fileName: string) => {
        const fileObj = files.find(
          (f) =>
            f.fileName === fileName
        );

        if (fileObj) {
          fileObj.status =
            "Scan Failed";

          fileObj.hasError = true;
          fileObj.statusCode = 400;
          fileObj.errorMessage =
            "File failed security scan.";
        }
      }
    );

    scannedFiles.forEach(
      (fileName: string) => {
        const processRequest =
          pendingProcessRequests.get(
            fileName
          );

        console.log(
          "[MultipartFileUploadComponent] scanned file:",
          fileName,
          "hasRequest:",
          !!processRequest
        );

        if (!processRequest) {
          return;
        }

        processRequest()
          .then((response: any) => {
            const fileObj =
              files.find(
                (f) =>
                  f.fileName ===
                  fileName
              );

            if (!fileObj) {
              return;
            }

            const extention =
              fileObj.file?.name
                .split(".")
                .pop();

            if (
              response.status === 200
            ) {
              props.onFileuploaded?.();

              if (
                extention?.toLowerCase() ===
                "zip"
              ) {
                fileObj.subFiles =
                  response.data;

                fileObj.hasError =
                  response.data?.some(
                    (x: any) =>
                      x.statusCode !==
                      "200"
                  );

                let status =
                  "Success";

                let statusCode = 200;

                if (
                  response.data?.some(
                    (x: any) =>
                      x.statusCode ===
                      "200"
                  ) &&
                  fileObj.hasError
                ) {
                  status =
                    "Partially Uploaded";
                } else if (
                  fileObj.hasError
                ) {
                  status =
                    "Failed";
                }

                if (
                  !fileObj.hasError &&
                  response.data.length >
                    0
                ) {
                  status =
                    "Success";

                  statusCode = 200;
                }

                fileObj.status =
                  status;

                fileObj.statusCode =
                  status ===
                  "Failed"
                    ? 400
                    : statusCode;
              } else {
                if (
                  response.data
                    .statusCode ===
                  "400"
                ) {
                  fileObj.status =
                    "Failed";

                  fileObj.statusCode =
                    400;

                  fileObj.hasError =
                    true;

                  fileObj.errorMessage =
                    response.data
                      .message ||
                    response.data
                      .value;
                } else {
                  fileObj.status =
                    "Success";

                  fileObj.statusCode =
                    200;

                  fileObj.hasError =
                    false;
                }
              }
            } else {
              fileObj.status =
                "Failed";

              fileObj.hasError =
                true;

              fileObj.statusCode =
                400;
            }

            setFiles([...files]);
          })
          .catch((error: any) => {
            const fileObj =
              files.find(
                (f) =>
                  f.fileName ===
                  fileName
              );

            if (fileObj) {
              console.error(
                "[MultipartFileUploadComponent] processRequest failed:",
                fileName,
                error
              );

              fileObj.status =
                "Failed";

              fileObj.hasError =
                true;

              fileObj.statusCode =
                400;

              fileObj.errorMessage =
                error.response?.data ||
                error.message;

              setFiles([...files]);
            }
          });
      }
    );

    setPendingProcessRequests(
      (prev) => {
        const next =
          new Map(prev);

        [
          ...scannedFiles,
          ...scanFailedFiles,
        ].forEach((name) =>
          next.delete(name)
        );

        return next;
      }
    );

    if (
      scanFailedFiles.length > 0
    ) {
      setFiles([...files]);
    }
  }, [
    isScanEnabled,
    scanInfo,
    pendingProcessRequests,
    files,
    props,
  ]);

  useEffect(() => {
    if (
      !filesInfo ||
      filesInfo.length === 0
    ) {
      return;
    }

    for (const fileObj of files) {
      const fileInfo =
        filesInfo.find(
          (f) =>
            f.fileName ===
            fileObj.fileName
        );

      if (fileInfo) {
        fileObj.filesCount =
          fileInfo.filesCount;
      }
    }

    setFiles([...files]);
  }, [filesInfo]);

  useEffect(() => {
    if (
      props.isReplace &&
      props.rowData
    ) {
      setReviewYear(
        props.rowData.reviewYear
      );

      setFileType(
        props.rowData.documentType
      );

      setproviderType(
        props.rowData
          .providerTypeCode
      );
    }

    httpService
      .get(
        ApiEndpoints.GetProviderTypes
      )
      .then((resp: any) =>
        setProviderTypeData(
          resp?.data
        )
      )
      .catch((error: any) =>
        console.error(
          "[MultipartFileUploadComponent] Error fetching provider types:",
          error
        )
      );
  }, []);

  useEffect(() => {
    if (reviewYear) {
      setReviewYearOption(
        reviewYear
      );

      resetFileSelection();
    }
  }, [reviewYear]);

  useEffect(() => {
    if (fileType) {
      resetFileSelection();
    }
  }, [fileType]);

  useEffect(() => {
    if (
      providerType &&
      providerType.trim() !== ""
    ) {
      void getLibraryLockFeature();
    }
  }, [providerType]);

  const getLibraryLockFeature =
    async () => {
      try {
        console.log(
          "[MultipartFileUploadComponent] Fetching data upload center lock status.",
          {
            providerType,
          }
        );

        const response: any =
          await httpService.get(
            `${ApiEndpoints.GetProviderFeature}/${providerType}/${Constants.DATA_UPLOAD_CENTER_LOCKED}`
          );

        if (
          response.status === 200
        ) {
          setIsDucLocked(
            !response?.data?.isEnabled
          );

          console.log(
            "[MultipartFileUploadComponent] Data upload center lock status fetched.",
            {
              providerType,
              isEnabled:
                response?.data
                  ?.isEnabled,
            }
          );
        }
      } catch (error: any) {
        setIsDucLocked(true);

        console.error(
          "[MultipartFileUploadComponent] Error fetching data upload center lock status.",
          {
            providerType,
            message:
              error?.message,
            stack: error?.stack,
            responseStatus:
              error?.response
                ?.status,
            cause: error?.cause,
          }
        );
      }
    };

  const getLatestMessage = (
    messages: any[]
  ): any => {
    const latestMessages =
      messages.reduce(
        (
          acc: any,
          message: any
        ) => {
          if (
            !acc[
              message.fileName
            ]
          ) {
            acc[
              message.fileName
            ] = message;
          } else if (
            new Date(
              message.timeStamp
            ) >
              new Date(
                acc[
                  message.fileName
                ].timeStamp
              ) ||
            new Date(
              message.timeStamp
            ).getTime() ===
              new Date(
                acc[
                  message.fileName
                ].timeStamp
              ).getTime()
          ) {
            acc[
              message.fileName
            ] = message;
          }

          return acc;
        },
        {}
      );

    const groupedMessages =
      Object.values(
        latestMessages
      ).reduce(
        (
          acc: any,
          message: any
        ) => {
          if (
            !acc[
              message.fileKey
            ]
          ) {
            acc[
              message.fileKey
            ] = [];
          }

          acc[
            message.fileKey
          ].push(message);

          return acc;
        },
        {}
      );

    return groupedMessages;
  };

  const emptyTemplate = () => (
    <>
      <p className="m-0 text-center">
        Drag and drop file to
        here to upload.
      </p>

      <div className="drag-upload-icon">
        <i className="pi pi-upload"></i>{" "}
      </div>
    </>
  );

  const handleFileSelect = (
    e: any
  ) => {
    const allowedExt =
      props.isBulkUpload &&
      props.allowedExtension
        ? props.allowedExtension
            .split(",")
            .map(
              (ext: string) =>
                ext
                  .trim()
                  .toLowerCase()
            )
        : [];

    const invalidFiles =
      e.files.filter(
        (file: any) => {
          const ext =
            "." +
            file.name
              .split(".")
              .pop()
              ?.toLowerCase();

          return (
            allowedExt.length >
              0 &&
            !allowedExt.includes(
              ext
            )
          );
        }
      );

    if (
      invalidFiles.length > 0
    ) {
      setErrorMessage(
        `Invalid file extension. Allowed: ${allowedExt.join(
          ", "
        )}`
      );

      setIsUploadDisabled(
        true
      );

      const failed =
        invalidFiles.map(
          (file: any) => ({
            fileName:
              file.name,
            status: "Failed",
            file,
            statusCode: 400,
          })
        );

      setFiles(failed);

      return;
    }

    setErrorMessage("");
    setIsUploadDisabled(false);

    const updated =
      e.files.map(
        (file: any) => ({
          fileName:
            file.name,
          status:
            "Pending",
          file,
        })
      );

    setFiles(updated);
  };

  const initiateMultipartUpload =
    async (
      fileName: string
    ): Promise<{
      fileKey: string;
      uploadId: string;
      isGuardDutyEnabled: boolean;
    }> => {
      try {
        const isReplace =
          props.isReplace
            ? true
            : false;

        const response =
          props.isBulkUpload
            ? await httpService.post(
                `${ApiEndpoints.InitiateMultipartBulkUpload}`,
                {
                  reviewYear:
                    reviewYear,
                  fileName,
                  providerType,
                  fileType,
                  isReplace,
                  connectionId:
                    getSafeConnectionId(),
                }
              )
            : await httpService.post(
                `${ApiEndpoints.InitiateMultipartUpload}`,
                {
                  fileName,
                  providerId:
                    props.providerId,
                  connectionId:
                    getSafeConnectionId(),
                  reviewYear,
                }
              );

        if (
          response.status !==
          200
        ) {
          throw new Error(
            "Failed to initiate multipart upload"
          );
        }

        const result =
          response.data as {
            fileKey: string;
            uploadId: string;
            isGuardDutyEnabled: boolean;
          };

        setIsScanEnabled(
          result.isGuardDutyEnabled ||
            false
        );

        return result;
      } catch (error: any) {
        const message =
          typeof error?.response
            ?.data === "string"
            ? error.response.data
            : error?.response
                ?.data?.message ||
              error?.message ||
              "Error initiating multipart upload";

        const wrappedError = new Error(message) as Error & { cause?: unknown };
        wrappedError.cause = error;
        throw wrappedError;
      }
    };

  const uploadChunk = async (
    fileKey: string,
    uploadId: string,
    fileChunk: Blob,
    chunkNumber: number,
    isLastChunk: boolean
  ): Promise<string> => {
    const formData =
      new FormData();

    formData.append(
      "fileKey",
      fileKey
    );

    formData.append(
      "uploadId",
      uploadId
    );

    formData.append(
      "fileStream",
      fileChunk
    );

    formData.append(
      "partNumber",
      chunkNumber.toString()
    );

    formData.append(
      "IsLastChunk",
      isLastChunk.toString()
    );

    const upload = async (
      retryCount: number = 0
    ): Promise<string> => {
      try {
        const response =
          await httpService.post(
            ApiEndpoints.UploadMultipartFile,
            formData
          );

        if (
          response.status !==
          200
        ) {
          throw new Error(
            "Failed to upload chunk"
          );
        }

        return response.data as string;
      } catch (error: any) {
        console.error(
          "[MultipartFileUploadComponent] Error uploading chunk:",
          {
            fileKey,
            chunkNumber,
            retryCount,
            message:
              error?.message,
            status:
              error?.response
                ?.status,
            cause:
              error?.cause,
          }
        );

        if (
          retryCount < 2
        ) {
          return upload(
            retryCount + 1
          );
        }

        await abortMultipartUpload(
          fileKey,
          uploadId
        );

        throw error;
      }
    };

    return upload();
  };

  const completeMultipartUpload =
    async (
      fileKey: string,
      uploadId: string,
      tags: string[]
    ) => {
      try {
        const data = {
          fileKey,
          uploadId,
          eTags: tags,
        };

        const response =
          await httpService.post(
            `${ApiEndpoints.CompleteMultipartUpload}`,
            data
          );

        if (
          response.status !==
          200
        ) {
          throw new Error(
            "Failed to complete multipart upload"
          );
        }
      } catch (error: any) {
        console.error(
          "[MultipartFileUploadComponent] Error completing multipart upload:",
          {
            fileKey,
            message:
              error?.message,
            status:
              error?.response
                ?.status,
            cause:
              error?.cause,
          }
        );

        await abortMultipartUpload(
          fileKey,
          uploadId
        );

        throw error;
      }
    };

  const abortMultipartUpload =
    async (
      fileKey: string,
      uploadId: string
    ) => {
      const data = {
        fileKey,
        uploadId,
      };

      await httpService.post(
        `${ApiEndpoints.AbortMultipartUpload}`,
        data
      );
    };

  const getFileMetaInfo =
    async (
      fileName: string
    ): Promise<FileMetaInfo> => {
      const url =
        props.isBulkUpload
          ? `${ApiEndpoints.GetFileMetadata}/${fileName}?isBulkUpload=true`
          : `${ApiEndpoints.GetFileMetadata}/${fileName}`;

      const response =
        await httpService.get(
          url
        );

      if (
        response.status !==
        200
      ) {
        throw new Error(
          "Failed to get file meta info"
        );
      }

      return response.data as FileMetaInfo;
    };

  const getTaskBatches = (
    tasks: any[]
  ): any => {
    const taskBatches = [];
    const batchSize = 4;

    for (
      let i = 0;
      i < tasks.length;
      i += batchSize
    ) {
      taskBatches.push(
        tasks.slice(
          i,
          i + batchSize
        )
      );
    }

    return taskBatches;
  };

  const uploadFileToCloud =
    async (
      file: any
    ): Promise<{
      fileKey: string;
      isScanEnabled: boolean;
    }> => {
      const chunkSize =
        7 * 1024 * 1024;

      const totalChunks =
        Math.ceil(
          file.size /
            chunkSize
        );

      const initResult =
        await initiateMultipartUpload(
          file.name
        );

      const tasks: any[] = [];

      for (
        let i = 0;
        i < totalChunks;
        i++
      ) {
        const start =
          i * chunkSize;

        const end =
          Math.min(
            start +
              chunkSize,
            file.size
          );

        const chunk =
          file.slice(
            start,
            end
          );

        tasks.push(() =>
          uploadChunk(
            initResult.fileKey,
            initResult.uploadId,
            chunk,
            i,
            totalChunks - i ===
              1
          )
        );
      }

      const taskBatches =
        getTaskBatches(tasks);

      const tags: string[] =
        [];

      for (
        let i = 0;
        i <
        taskBatches.length;
        i++
      ) {
        const result =
          await Promise.all(
            taskBatches[i].map(
              (task: any) =>
                task()
            )
          );

        tags.push(
          ...result
        );
      }

      await completeMultipartUpload(
        initResult.fileKey,
        initResult.uploadId,
        tags
      );

      return {
        fileKey:
          initResult.fileKey,
        isScanEnabled:
          initResult.isGuardDutyEnabled,
      };
    };

  const processFile = async (
    fileName: string,
    fileKey: string,
    fileMetaInfo: FileMetaInfo | null,
    isZipFile: boolean
  ) => {
    if (fileMetaInfo) {
      fileMetaInfo.documentType =
        props.isBulkUpload
          ? fileType
          : fileMetaInfo.documentType;
    }

    const data: any = {
      reviewYear:
        props.isBulkUpload
          ? reviewYearState
          : reviewYear,
      fileKey,
      providerFileMetaInfo:
        fileMetaInfo,
      providerId:
        props.providerId,
      connectionId:
        getSafeConnectionId(),
      fileName,
      providerType:
        providerType ?? "",
      isBulkUpload:
        props.isBulkUpload
          ? true
          : false,
    };

    const url =
      isZipFile
        ? ApiEndpoints.ProcessZipFile
        : ApiEndpoints.ProcessFile;

    return httpService.post(
      `${url}`,
      data
    );
  };

  const uploadAndProcess =
    async (file: any) => {
      const isZipFile =
        file.name
          ?.toLowerCase()
          .endsWith(".zip");

      let fileMetaInfo:
        | FileMetaInfo
        | null = null;

      if (!isZipFile) {
        fileMetaInfo =
          await getFileMetaInfo(
            file.name
          );
      }

      const {
        fileKey,
        isScanEnabled:
          guardDutyEnabled,
      } =
        await uploadFileToCloud(
          file
        );

      if (
        !guardDutyEnabled
      ) {
        return processFile(
          file.name,
          fileKey,
          fileMetaInfo,
          isZipFile
        );
      }

      const pendingRequest =
        new Map(
          pendingProcessRequests
        );

      pendingRequest.set(
        file.name,
        () =>
          processFile(
            file.name,
            fileKey,
            fileMetaInfo,
            isZipFile
          )
      );

      setPendingProcessRequests(
        pendingRequest
      );

      return {
        status: 202,
        data: {
          message:
            "File is pending for scan",
        },
      };
    };

  const customUpload =
    async (e: any) => {
      if (
        props.isBulkUpload &&
        (!reviewYearState ||
          !providerType ||
          !fileType)
      ) {
        setErrorMessage(
          "Please select Review Year, PBC Type and File Type for uploading file"
        );

        return;
      }

      setErrorMessage("");

      const updatedFiles =
        e.files.map(
          (file: any) => ({
            fileName:
              file.name,
            status:
              "Uploading",
            file,
          })
        );

      setFiles(
        updatedFiles
      );

      for (const fileObj of updatedFiles) {
        const extention =
          fileObj.file?.name
            .split(".")
            .pop();

        uploadAndProcess(
          fileObj.file
        )
          .then(
            (response: any) => {
              if (
                response.status ===
                200
              ) {
                props.onFileuploaded?.();

                if (
                  extention?.toLowerCase() ===
                  "zip"
                ) {
                  fileObj.subFiles =
                    response.data;

                  fileObj.hasError =
                    response.data?.some(
                      (x: any) =>
                        x.statusCode !==
                        "200"
                    );

                  let status =
                    "Success";

                  let statusCode =
                    200;

                  status =
                    response.data?.some(
                      (x: any) =>
                        x.statusCode ===
                        "200"
                    ) &&
                    fileObj.hasError
                      ? "Partially Uploaded"
                      : "Failed";

                  if (
                    !fileObj.hasError &&
                    response.data
                      .length > 0
                  ) {
                    status =
                      "Success";

                    statusCode =
                      200;
                  }

                  fileObj.status =
                    status;

                  fileObj.statusCode =
                    status ===
                    "Failed"
                      ? 400
                      : statusCode;
                } else {
                  if (
                    response.data
                      .statusCode ===
                    "400"
                  ) {
                    fileObj.status =
                      "Failed";

                    fileObj.statusCode =
                      400;

                    fileObj.hasError =
                      true;

                    fileObj.errorMessage =
                      response.data
                        .message ||
                      response.data
                        .value;
                  } else {
                    fileObj.status =
                      "Success";

                    fileObj.statusCode =
                      200;

                    fileObj.hasError =
                      false;
                  }
                }

                setFiles([
                  ...updatedFiles,
                ]);

                return;
              }

              if (
                response.status ===
                202
              ) {
                fileObj.status =
                  "Scan Inprogress";

                fileObj.statusCode =
                  202;

                setFiles([
                  ...updatedFiles,
                ]);

                return;
              }

              fileObj.status =
                "Failed";

              fileObj.hasError =
                true;

              fileObj.statusCode =
                400;

              setFiles([
                ...updatedFiles,
              ]);
            }
          )
          .catch(
            (err: AxiosError) => {
              if (
                err.response
                  ?.status ===
                504
              ) {
                fileObj.status =
                  "Processing...";

                fileObj.statusCode =
                  302;

                setFiles([
                  ...updatedFiles,
                ]);

                return;
              }

              fileObj.status =
                "Failed";

              fileObj.hasError =
                true;

              fileObj.statusCode =
                400;

              fileObj.errorMessage =
                (err.response
                  ?.data as any) ||
                err.message;

              setFiles([
                ...updatedFiles,
              ]);
            }
          );
      }
    };

  const getBackgroundColorByStatucCode = (
    statusCode: number
  ) => {
    switch (
      statusCode
    ) {
      case 200:
        return "green";
      case 400:
        return "#d32f2f";
      case 102:
      case 202:
      case 302:
        return "#007cb0";
      default:
        return "white";
    }
  };

  const getSevirityByStatusCode = (
    statusCode: number
  ) => {
    switch (
      statusCode
    ) {
      case 200:
        return "success";
      case 400:
        return "danger";
      case 102:
        return "info";
      default:
        return "info";
    }
  };

  const getStatusByStatusCode = (
    statusCode: number
  ) => {
    switch (
      statusCode
    ) {
      case 200:
        return "Success";
      case 400:
        return "Failed";
      case 102:
        return "Uploading...";
      case 202:
        return "Scan Inprogress...";
      case 302:
        return "Processing...";
      case 401:
        return "Scan Failed";
      default:
        return "";
    }
  };

  const getParentFileStatus = (
    fileObj:
      | FileUploInfo
      | undefined
  ) => {
    if (!fileObj) {
      return "";
    }

    const status =
      fileObj.status;

    let percentageStr =
      "0%";

    if (
      fileObj.filesCount >
      0
    ) {
      const processedFilesCount =
        fileObj.subFiles?.filter(
          (x: any) =>
            x.statusCode !==
            102
        ).length ?? 0;

      const percentage =
        (processedFilesCount /
          fileObj.filesCount) *
        100;

      percentageStr =
        percentage.toFixed(
          2
        ) + "%";

      return (
        <span>
          {percentage === 100
            ? status
            : percentageStr}
        </span>
      );
    }

    return (
      <span>
        {status}
      </span>
    );
  };

  const itemTemplate = (
    file: any,
    fileProps: any
  ) => {
    const fileinfo =
      files.find(
        (f) =>
          f.fileName ===
          file?.name
      );

    const isZipFile =
      file.name
        ?.toLowerCase()
        .endsWith(".zip");

    return (
      <div>
        <div
          className="odp-file-upload-row"
          style={{
            borderRadius:
              "10px",
            marginBottom:
              "10px",
            padding: "1rem",
          }}
        >
          <div
            style={{
              textAlign:
                "left",
              alignContent:
                "top",
            }}
          >
            <span>
              {file.name}
            </span>
          </div>

          <div
            style={{
              textAlign:
                "left",
              alignContent:
                "top",
            }}
          >
            <Tag
              value={
                fileProps.formatSize
              }
              severity={getSevirityByStatusCode(
                fileinfo?.statusCode ??
                  0
              )}
              className="px-3 py-2"
            />
          </div>

          <div
            style={{
              textAlign:
                "left",
              alignContent:
                "top",
            }}
          >
            {fileinfo?.status !==
              "Pending" && (
              <>
                <Tag
                  value={
                    isZipFile
                      ? getParentFileStatus(
                          fileinfo
                        )
                      : getStatusByStatusCode(
                          fileinfo?.statusCode ??
                            102
                        )
                  }
                  severity={getSevirityByStatusCode(
                    fileinfo?.statusCode ??
                      0
                  )}
                  className="px-3 py-2"
                />

                {fileinfo?.hasError &&
                  fileinfo?.errorMessage && (
                    <Message
                      severity="error"
                      text={
                        fileinfo.errorMessage
                      }
                    />
                  )}
              </>
            )}
          </div>

          <div
            style={{
              alignItems:
                "left",
              alignContent:
                "top",
              marginTop:
                "8%",
            }}
          >
            <button
              className="pi pi-times close-button"
              onClick={() => {
                fileProps.onRemove();

                setIsUploadDisabled(
                  false
                );

                setErrorMessage(
                  ""
                );
              }}
            />
          </div>
        </div>

        {fileinfo?.subFiles &&
          fileinfo.subFiles
            .length > 0 && (
            <div>
              {fileinfo.subFiles
                .sort(
                  (
                    a: any,
                    b: any
                  ) =>
                    a.statusCode -
                    b.statusCode
                )
                .map(
                  (
                    subFile: any,
                    idx: number
                  ) => (
                    <div
                      key={`${
                        subFile?.fileName ??
                        "sub"
                      }-${idx}`}
                      className="sub-file-info-table three-column-div"
                      style={{
                        background:
                          getBackgroundColorByStatucCode(
                            subFile.statusCode
                          ),
                        color:
                          "white",
                      }}
                    >
                      <div
                        style={{
                          textAlign:
                            "left",
                          alignContent:
                            "top",
                        }}
                      >
                        {
                          subFile?.fileName
                        }
                      </div>

                      <div
                        style={{
                          textAlign:
                            "center",
                          alignContent:
                            "top",
                        }}
                      >
                        Status:{" "}
                        {getStatusByStatusCode(
                          subFile.statusCode
                        )}
                      </div>

                      <div
                        style={{
                          textAlign:
                            "left",
                          alignContent:
                            "top",
                        }}
                      >
                        {subFile.statusCode ===
                          400 &&
                          subFile?.message && (
                            <>
                              Error:{" "}
                              <span className="error-message">
                                {
                                  subFile.message
                                }
                              </span>
                            </>
                          )}
                      </div>
                    </div>
                  )
                )}
            </div>
          )}
      </div>
    );
  };

  const onChangeProviderType = (
    event: any
  ) => {
    setproviderType(
      event.value
    );

    if (
      props.setProviderType
    ) {
      props.setProviderType(
        event.value
      );
    }

    setFileType("");
    setfileTypeData([]);

    resetFileSelection();
  };

  const onChangeReviewYear = (
    event: any
  ) => {
    setReviewYear(
      event.value
    );

    setFileType("");
    resetFileSelection();

    if (!providerType) {
      setfileTypeData([]);
      return;
    }

    httpService
      .get(
        `${ApiEndpoints.GetDocumentTypeForProvider}/${providerType}/${event.value}`
      )
      .then((resp: any) => {
        setfileTypeData(
          resp?.data || []
        );
      })
      .catch((error: any) => {
        setfileTypeData([]);

        console.error(
          "[MultipartFileUploadComponent] Error fetching file types:",
          {
            providerType,
            reviewYear:
              event.value,
            message:
              error?.message,
            status:
              error?.response
                ?.status,
            cause:
              error?.cause,
          }
        );
      });
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection:
            "row",
          justifyContent:
            "space-evenly",
        }}
        className="card"
      >
        {props.isBulkUpload &&
          !props.isReplace && (
            <div
              style={{
                display:
                  "flex",
                flexDirection:
                  "column",
                justifyContent:
                  "space-evenly",
              }}
            >
              <Dropdown
                placeholder="PBC Type"
                options={providerTypeData.map(
                  (x) => ({
                    label:
                      x.providerType,
                    value:
                      x.providerCode,
                  })
                )}
                onChange={
                  onChangeProviderType
                }
                value={
                  providerType
                }
                disabled={
                  !isAdministrator() ||
                  props.isActiveReview
                }
              />

              <Dropdown
                placeholder="Review Year"
                options={[
                  reviewYearOption,
                ]}
                onChange={
                  onChangeReviewYear
                }
                value={
                  reviewYearState
                }
                disabled={
                  !isAdministrator() ||
                  props.isActiveReview ||
                  isDucLocked
                }
              />

              <Dropdown
                placeholder="Select File Type"
                options={fileTypeData.map(
                  (x) =>
                    x.documentType
                )}
                onChange={(
                  event
                ) => {
                  setFileType(
                    event.value
                  );

                  resetFileSelection();
                }}
                value={fileType}
                disabled={
                  !isAdministrator() ||
                  props.isActiveReview ||
                  isDucLocked
                }
              />
            </div>
          )}

        <div
          className="card"
          style={{
            margin:
              "0 auto",
            padding:
              "10px",
          }}
        >
          {errorMessage && (
            <Message
              severity="error"
              text={
                errorMessage
              }
            />
          )}

          <FileUpload
            ref={uploadRef}
            multiple={
              props.isBulkUpload
                ? false
                : true
            }
            maxFileSize={
              Constants.FILE_UPLOAD_SIZE_LIMIT
            }
            onError={(
              event: any
            ) =>
              setErrorMessage(
                "Error occured while uploading file: " +
                  event[
                    "xhr"
                  ]
                    .responseText
              )
            }
            name="demo[]"
            uploadHandler={
              customUpload
            }
            customUpload
            onSelect={
              handleFileSelect
            }
            emptyTemplate={
              emptyTemplate
            }
            itemTemplate={
              itemTemplate
            }
            disabled={
              isUploadDisabled ||
              props.isActiveReview ||
              isDucLocked
            }
            accept={
              props.isBulkUpload
                ? props.allowedExtension
                : undefined
            }
          />
        </div>
      </div>
    </>
  );
};

export default MultipartFileUploadComponent;